export const CHECKOUT_URL = 'https://rehabilitation-en.impultienda.ar/checkout/';

export const images = {
  mainMockup: 'https://cdn.magicpatterns.com/uploads/vAAK5QG75Z5LhFNBgKej2Z/REHABILITATION-EN.png',
  doctor: 'https://cdn.magicpatterns.com/uploads/333u3KdyU44izURnAMBo9t/DR._NATHAN_BROOKS_-_ORTHO.png',
};

export const methodSteps = [
  { word: 'Assess', text: 'Organize the available clinical information.' },
  { word: 'Define', text: 'Document the problems and treatment objectives.' },
  { word: 'Prioritize', text: 'Identify priorities, dependencies, and unresolved questions.' },
  { word: 'Map', text: 'Organize the proposed treatment phases.' },
  { word: 'Sequence', text: 'Document the intended order of procedures.' },
  { word: 'Review', text: 'Check the completeness and consistency of your plan.' },
];

export const beforeItems = [
  'You look at a complex rehabilitation case and struggle to decide where to begin organizing the information.',
  'Your clinical findings, photographs, notes, and treatment considerations are scattered across different documents.',
  'You move between functional, aesthetic, restorative, and prosthetic considerations without a consistent framework.',
  'You know several possible approaches, but organizing them into a coherent sequence takes time.',
  'Important questions or dependencies can remain undocumented until later in the process.',
  'Every complex case feels like starting from a blank page.',
];

export const afterItems = [
  'You begin with a structured case assessment.',
  'You organize the available findings into a clear problem list.',
  'You document treatment objectives before finalizing your proposed approach.',
  'You map priorities, dependencies, and unresolved questions.',
  'You organize the proposed treatment into clearly documented phases.',
  'You have a visual roadmap you can revisit, update, and use when discussing the case with your team.',
];

export type PainIcon = 'start' | 'time' | 'connect' | 'scattered' | 'review' | 'rebuild';

export const painPoints: { icon: PainIcon; title: string; text: string }[] = [
  {
    icon: 'start',
    title: 'You have a complex case and aren’t sure where to start',
    text: 'A patient presents with multiple restorative, functional, or aesthetic concerns, and you need a better way to organize the information before planning.',
  },
  {
    icon: 'time',
    title: 'You spend too much time organizing your notes',
    text: 'You review photographs, clinical records, previous notes, and materials before you can bring everything into one coherent overview.',
  },
  {
    icon: 'connect',
    title: 'You understand the concepts, but connecting them is difficult',
    text: 'Turning related considerations into a structured treatment roadmap takes considerable mental effort.',
  },
  {
    icon: 'scattered',
    title: 'Your treatment planning information is scattered',
    text: 'You have the information, but not in a single framework that makes priorities, dependencies, and open questions easy to review.',
  },
  {
    icon: 'review',
    title: 'You want a better way to review your plans',
    text: 'You want a repeatable process to check whether information, objectives, phases, and follow-up have been documented.',
  },
  {
    icon: 'rebuild',
    title: 'Every complex case makes you rebuild your process',
    text: 'You don’t need another textbook. You need a reusable structure that helps you organize what you already know.',
  },
];

export type ToolIcon = 'protocol' | 'checklist' | 'framework' | 'matrix' | 'roadmap';

export const tools: { icon: ToolIcon; title: string; text: string }[] = [
  { icon: 'protocol', title: 'Structured Case-Planning Protocols', text: 'Practical frameworks to organize information and document each stage of your planning process.' },
  { icon: 'checklist', title: 'Professional Case Checklists', text: 'Reusable checklists to review documentation, spot outstanding questions, and stay consistent.' },
  { icon: 'framework', title: 'Clinical Planning Decision Frameworks', text: 'Visual frameworks to organize considerations, document alternatives, and flag what needs more evaluation.' },
  { icon: 'matrix', title: 'Case-Mapping Matrices', text: 'Findings → Problems → Objectives → Dependencies → Proposed Phases → Review.' },
  { icon: 'roadmap', title: 'Visual Treatment Roadmaps', text: 'Reusable planning sheets that bring every component of a case into one structured overview.' },
];

export const guaranteeItems = [
  'Explore all six volumes',
  'Review the planning frameworks',
  'Examine the case-mapping tools',
  'Use the checklists and worksheets',
  'Review the educational examples',
  'Explore the one-page case presentation',
];

export const aboutConsiderations = [
  'Clinical findings',
  'Existing restorations',
  'Functional considerations',
  'Aesthetic objectives',
  'Restorative requirements',
  'Prosthetic planning',
  'Dependencies between procedures',
  'Communication and documentation',
  'Review and follow-up',
];

export const finalFlow = [
  'Clinical Information',
  'Findings',
  'Problems',
  'Objectives',
  'Priorities',
  'Proposed Phases',
  'Review',
];

export const faqs = [
  { q: 'How will I receive the material?', a: 'Immediately after your payment, you’ll receive instructions by email explaining how to access your digital materials. The six volumes and five bonuses are delivered digitally.' },
  { q: 'Is Oral Rehabilitation Blueprint™ a course?', a: 'No. It’s a practical digital toolkit containing six instructional volumes, visual planning frameworks, reusable worksheets, checklists, and case-organization tools. The emphasis is on organizing and documenting information rather than watching hours of recorded lessons.' },
  { q: 'Is this intended for general dentists or prosthodontic specialists?', a: 'The materials are intended for dental professionals involved in oral rehabilitation and prosthetic treatment planning. Your training, professional scope, and the complexity of the case will determine how the tools can be applied in your practice.' },
  { q: 'Does this system diagnose patients or recommend treatments automatically?', a: 'No. Oral Rehabilitation Blueprint™ is an organizational and educational resource. It does not independently diagnose patients, determine treatment indications, or replace a comprehensive clinical assessment.' },
  { q: 'Can I reuse the templates?', a: 'Yes. The worksheets and planning frameworks are designed to be reused for different cases, subject to the terms of use provided with the product. Always protect patient confidentiality and follow applicable privacy requirements.' },
  { q: 'Can I use the system for complex rehabilitation cases?', a: 'The templates can help you organize the information and questions involved in complex cases. However, they do not replace specialist training, interdisciplinary collaboration, or additional clinical assessment when required.' },
  { q: 'Is this a one-time payment?', a: 'Yes. The advertised price is a one-time payment of $27 USD, with no recurring monthly fee.' },
  { q: 'Do I receive lifetime access?', a: 'Yes. The offer includes ongoing access to the purchased materials under the applicable terms of service.' },
  { q: 'What if I don’t like it?', a: 'Your purchase is covered by the advertised seven-day money-back guarantee. Review the guarantee terms provided at checkout and contact us within the stated period if you decide the system isn’t right for you.' },
];

export const buyers = [
  { name: 'María Fernanda', country: 'Mexico', flag: '🇲🇽' },
  { name: 'Carlos Andrés', country: 'Colombia', flag: '🇨🇴' },
  { name: 'Lucía', country: 'Argentina', flag: '🇦🇷' },
  { name: 'Javier', country: 'Spain', flag: '🇪🇸' },
  { name: 'Valentina', country: 'Chile', flag: '🇨🇱' },
  { name: 'Diego', country: 'Peru', flag: '🇵🇪' },
  { name: 'Camila', country: 'Uruguay', flag: '🇺🇾' },
  { name: 'Andrés Felipe', country: 'Ecuador', flag: '🇪🇨' },
  { name: 'Gabriela', country: 'Costa Rica', flag: '🇨🇷' },
  { name: 'Ricardo', country: 'Dominican Republic', flag: '🇩🇴' },
  { name: 'Sofía', country: 'Guatemala', flag: '🇬🇹' },
  { name: 'Alejandro', country: 'Panama', flag: '🇵🇦' },
  { name: 'Daniela', country: 'Paraguay', flag: '🇵🇾' },
  { name: 'Mateo', country: 'Bolivia', flag: '🇧🇴' },
  { name: 'Isabel', country: 'Puerto Rico', flag: '🇵🇷' },
];
